import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ArrowLeft, Printer, FileDown, Pencil, FileSignature, MoreVertical, Loader2, Images, Link2, Gift, Share2, MessageCircle, Mail, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const fmt = (n, c = "EUR") =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: c }).format(n || 0);

const fmtDate = (s) => {
  if (!s) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
};

const statusLabel = { draft: "Bozza", sent: "Inviato", signed: "Firmato" };

const NOLEGGIO_COVER = [
  {
    title: "86BIT OfficeRelax",
    paras: [
      "Da sempre, 86BIT si impegna a selezionare e proporre ai suoi clienti i migliori strumenti informatici e le più avanzate tecnologie presenti sul mercato.",
      "86BIT OfficeRelax è la soluzione per rendere certi i costi della tua struttura informatica, garantendoti al contempo di disporre sempre di attrezzatura aggiornata ed allo stato dell'arte. È un servizio che destiniamo esclusivamente alle aziende altamente referenziate che, oltre agli evidenti vantaggi consistenti nel «mettere sotto controllo» i propri costi, possono e vogliono cogliere le opportunità fiscali del noleggio e di una gestione burocratica altamente semplificata e snella nell'erogazione del servizio.",
      "86BIT OfficeRelax è il nostro servizio di noleggio operativo, che può riguardare il solo hardware oppure l'hardware ed il software.",
    ],
    note: "Tutti i prodotti sono ricondizionati con 60 mesi di garanzia in gestione a noleggio. Materiale di fascia alta, selezione business, per garantire prestazioni e affidabilità superiori.",
  },
  {
    title: "I Vantaggi",
    paras: ["La più ampia gamma di prodotti, soluzioni e servizi disponibili sul mercato IT, con architetture aperte a garanzia della massima flessibilità e del più elevato ritorno sugli investimenti."],
    bullets: [
      "Canone deducibile al 100% grazie alla «fattura di servizio» che riceverete.",
      "Sgrava da qualsiasi onere di manutenzione hardware delle macchine.",
      "Assicura la sostituzione della macchina in caso di malfunzionamenti gravi.",
      "Permette di inserire nel budget costi certi per il parco macchine.",
      "Garantisce il rinnovo del parco macchine ogni tre anni senza affrontare costi aggiuntivi.",
      "Permette di disporre subito di tutte le macchine, dell'installazione e del relativo software senza affrontare costi incerti.",
      "Migliora la pianificazione del budget aziendale.",
      "Canone fisso: nessun rischio di spese impreviste per tutta la durata del contratto.",
      "Esenzione IRAP.",
      "Sgravi fiscali: i dispositivi noleggiati sono di proprietà di terzi e pertanto non risultano iscritti a cespite e quindi non tassabili.",
      "Facoltà, a fine periodo, di rinnovare il contratto con gli stessi o con nuovi dispositivi, ricalcolando il canone di noleggio.",
      "Recesso dal contratto restituendo i prodotti al fornitore, senza spese di istruttoria né adeguamento ISTAT sui canoni.",
      "Possibilità di chiusura del contratto con riscatto del materiale.",
    ],
  },
  {
    title: "Cosa Consigliamo",
    paras: ["Le tue idee diventano realtà. Il noleggio operativo è la soluzione migliore per raggiungere gli obiettivi della tua azienda: utilizza subito beni strumentali, tecnologie e attrezzature senza vincolare il tuo capitale."],
  },
  {
    title: "Come",
    paras: ["Sensibilizzare, rendere consapevole e guidare il nostro cliente sulla scelta del prodotto informatico più adatto alle proprie esigenze di utilizzo è il nostro unico pensiero."],
  },
];

const SPEC_FIELDS = [
  ["processore", "Processore"],
  ["ram", "RAM"],
  ["hdd", "HDD"],
  ["monitor", "Monitor"],
  ["scheda_video", "Scheda Video"],
  ["sistema_operativo", "Sist. Operativo"],
];

function footerLines(c) {
  const l1 = [];
  if (c.legal_name) l1.push(c.legal_name);
  if (c.vat) l1.push(`Codice Fiscale e P.IVA ${c.vat}`);
  else if (c.fiscal_code) l1.push(`Codice Fiscale ${c.fiscal_code}`);
  if (c.share_capital) l1.push(`Capitale sociale ${c.share_capital}`);
  if (c.reg_imprese) l1.push(`Reg. Imprese di ${c.reg_imprese}`);
  const l2 = [];
  if (c.rea) l2.push(`REA n. ${c.rea}`);
  if (c.address) l2.push(c.address);
  if (c.phone) l2.push(`Tel. ${c.phone}`);
  if (c.email) l2.push(c.email);
  if (c.website) l2.push(c.website);
  return [l1.join(" - "), l2.join(" - ")];
}

export default function QuoteView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [company, setCompany] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [ds, setDs] = useState(null); // {status, status_it, ...}
  const [dsSending, setDsSending] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailData, setEmailData] = useState({ to: "", cc: "", subject: "", body: "" });

  const openEmailDialog = async () => {
    setEmailOpen(true);
    try {
      const { data } = await api.get(`/quotes/${id}/email-defaults`);
      setEmailData({ to: data.to || "", cc: data.cc || "", subject: data.subject || "", body: data.body || "" });
      if (!data.m365_configured) toast.warning("Microsoft 365 non configurato: vai in Setup per abilitare l'invio.");
    } catch {
      toast.error("Impossibile caricare i dati email");
    }
  };

  const sendEmail = async () => {
    if (!emailData.to.trim()) { toast.error("Inserisci l'email del destinatario"); return; }
    setEmailSending(true);
    try {
      const { data } = await api.post(`/quotes/${id}/send-email`, emailData);
      toast.success(data.detail || "Email inviata");
      if (data.status) setQuote((q) => (q ? { ...q, status: data.status } : q));
      setEmailOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore invio email");
    } finally {
      setEmailSending(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get(`/quotes/${id}`),
      api.get("/company"),
    ]).then(([q, c]) => { setQuote(q.data); setCompany(c.data); })
      .catch(() => { toast.error("Preventivo non trovato"); navigate("/quotes"); });
  }, [id, navigate]);

  useEffect(() => {
    api.get(`/quotes/${id}/docusign/status`).then((r) => setDs(r.data)).catch(() => {});
  }, [id]);

  const sendDocuSign = async () => {
    setDsSending(true);
    try {
      const { data } = await api.post(`/quotes/${id}/docusign/send`);
      setDs(data);
      toast.success("Preventivo inviato al cliente tramite DocuSign");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 412 && detail?.consent_url) {
        toast.error("Autorizzazione DocuSign mancante. Apri il link di consenso e riprova.");
        window.open(detail.consent_url, "_blank");
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore invio DocuSign");
      }
    } finally {
      setDsSending(false);
    }
  };

  const downloadSignedPdf = async () => {
    try {
      const res = await api.get(`/quotes/${id}/docusign/signed-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Documento firmato non ancora disponibile");
    }
  };

  const [showcaseLoading, setShowcaseLoading] = useState(false);

  const generateShowcase = async () => {
    setShowcaseLoading(true);
    try {
      const { data } = await api.post(`/quotes/${id}/showcase/generate`);
      setQuote((q) => ({ ...q, public_token: data.public_token, has_showcase: true }));
      const url = `${window.location.origin}/vetrina/${data.public_token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      if (data.image_quota_blocked) {
        toast.success(`Vetrina creata (${data.images_generated} immagini AI). Quota immagini Gemini esaurita: usate le foto disponibili. Link copiato.`);
      } else {
        toast.success(`Vetrina creata con ${data.images_generated} immagini. Link copiato negli appunti.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella generazione della vetrina");
    } finally {
      setShowcaseLoading(false);
    }
  };

  const [shareOpen, setShareOpen] = useState(false);
  const showcaseUrl = quote?.public_token ? `${window.location.origin}/vetrina/${quote.public_token}` : "";
  // Share link routes through the backend OG-preview endpoint (rich preview on WhatsApp/LinkedIn),
  // which then redirects real users to the interactive vetrina.
  // Always build it from the current origin (same host serves /api via ingress) → absolute & valid
  // in both preview and production, never "undefined".
  const shareUrl = quote?.public_token
    ? `${window.location.origin}/api/share/${quote.public_token}`
    : "";
  const shareMessage =
    `Ciao! 👋\n\n` +
    `Come promesso, ecco la *vetrina interattiva* della proposta che abbiamo preparato per te${quote?.title ? `: "${quote.title}"` : ""}.\n\n` +
    `Sfoglia i prodotti selezionati con foto e tutti i dettagli direttamente dal tuo telefono 👇\n` +
    `${shareUrl}\n\n` +
    `Restiamo a tua completa disposizione per qualsiasi domanda. 😊\n` +
    `Un caro saluto,\nIl team 86BIT`;
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, "_blank");
  const shareEmail = () => {
    const subject = encodeURIComponent(`Vetrina prodotti – ${quote?.title || "Offerta 86BIT"}`);
    const body = encodeURIComponent(shareMessage);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const copyShareLink = async () => {
    await navigator.clipboard.writeText(shareUrl).catch(() => {});
    toast.success("Link vetrina copiato negli appunti");
  };

  const fetchPdfBlob = async () => {
    const res = await api.get(`/quotes/${id}/pdf`, { responseType: "blob" });
    return new Blob([res.data], { type: "application/pdf" });
  };

  const printPdf = async () => {
    setPrinting(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch {
            window.open(url, "_blank");
          }
        }, 400);
      };
      setTimeout(() => { URL.revokeObjectURL(url); iframe.remove(); }, 60000);
    } catch {
      toast.error("Errore durante la generazione del PDF");
    } finally {
      setPrinting(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const blob = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${quote.number || "preventivo"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF scaricato");
    } catch {
      toast.error("Errore durante la generazione del PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!quote || !company) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const accent = company.brand_color || "#5B9BD5";
  const lineTotal = (i) => (i.price || 0) * (i.quantity || 1) * (1 - (i.discount || 0) / 100);
  const subtotal = (quote.items || []).reduce((s, i) => s + lineTotal(i), 0);
  const tax = subtotal * ((quote.tax_rate || 0) / 100);
  const total = subtotal + tax;
  const itemAcq = (i) => i.acquisition || quote.offer_type || "acquisto";
  const itemMonths = (i) => Number(i.rental_months) || Number(quote.rental_months) || 0;
  const nolItems = (quote.items || []).filter((i) => itemAcq(i) === "noleggio");
  const acqItems = (quote.items || []).filter((i) => itemAcq(i) !== "noleggio");
  const hasNoleggio = nolItems.length > 0;
  const nolFee = nolItems.reduce((s, i) => s + (itemMonths(i) ? lineTotal(i) / itemMonths(i) : 0), 0);
  const acqTotal = acqItems.reduce((s, i) => s + lineTotal(i), 0);
  const c = quote.currency || "EUR";
  const [f1, f2] = footerLines(company);

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      {/* Toolbar */}
      <div className="max-w-3xl mx-auto mb-6 flex items-center justify-between no-print flex-wrap gap-3">
        <button onClick={() => navigate("/quotes")} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Preventivi
        </button>
        <div className="flex items-center gap-2">
          <Link to={`/quotes/${id}/edit`}>
            <Button variant="outline" size="sm" data-testid="edit-quote-button">
              <Pencil className="w-4 h-4 mr-2" /> Modifica
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={printPdf} disabled={printing} data-testid="print-button">
            {printing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />} Stampa
          </Button>
          <Button size="sm" onClick={downloadPdf} disabled={downloading} data-testid="export-pdf-button" className="bg-blue-600 hover:bg-blue-700">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />} Esporta PDF
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" data-testid="more-actions-button"><MoreVertical className="w-4 h-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="send-email-action" onClick={openEmailDialog}>
                <Mail className="w-4 h-4 mr-2" /> Invia offerta via email
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="docusign-action" disabled={dsSending} onClick={sendDocuSign}>
                <FileSignature className="w-4 h-4 mr-2" />
                {ds && ds.status && ds.status !== "not_sent" ? "Reinvia con DocuSign" : "Invia con DocuSign"}
              </DropdownMenuItem>
              {ds && ds.status === "completed" && (
                <DropdownMenuItem data-testid="docusign-download-signed" onClick={downloadSignedPdf}>
                  <FileDown className="w-4 h-4 mr-2" /> Scarica PDF firmato
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="showcase-generate" disabled={showcaseLoading} onClick={generateShowcase}>
                {showcaseLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Images className="w-4 h-4 mr-2" />}
                {quote.has_showcase || quote.public_token ? "Rigenera vetrina prodotti" : "Genera vetrina prodotti"}
              </DropdownMenuItem>
              {(quote.has_showcase || quote.public_token) && (
                <DropdownMenuItem data-testid="showcase-share" onClick={() => setShareOpen(true)}>
                  <Share2 className="w-4 h-4 mr-2" /> Condividi vetrina
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {ds && ds.status && ds.status !== "not_sent" && (
        <div className="max-w-3xl mx-auto mb-6 no-print" data-testid="docusign-status-banner">
          <div className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-lg px-4 py-2.5 shadow-sm">
            <FileSignature className="w-4 h-4 text-blue-600" />
            <span className="text-slate-600">Stato firma DocuSign:</span>
            <span className={`font-semibold ${ds.status === "completed" ? "text-emerald-600" : ds.status === "declined" ? "text-red-600" : "text-amber-600"}`}>
              {ds.status_it || ds.status}
            </span>
          </div>
        </div>
      )}

      {/* Cover page sheet */}
      {quote.cover_enabled && (
        <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-lg shadow-sm flex flex-col min-h-[1123px] mb-6 print-area"
          data-testid="cover-sheet">
          <div className="p-[48px] flex flex-col flex-1">
            <div className="flex items-center pb-6 mb-2 gap-5" style={{ borderBottom: `1.5px solid ${accent}` }}>
              {company.logo
                ? <img src={company.logo} alt="Logo" className="h-[72px] w-auto object-contain max-w-[300px]" data-testid="cover-company-logo" />
                : <span className="font-heading font-extrabold text-2xl text-slate-900">{company.company_name || "La tua azienda"}</span>}
              {quote.client_logo && (
                <>
                  <div className="w-px h-[72px] shrink-0" style={{ backgroundColor: accent }} />
                  <img src={quote.client_logo} alt="Logo cliente" className="h-[72px] w-auto object-contain max-w-[240px]" data-testid="cover-client-logo" />
                </>
              )}
            </div>
            <div className="flex-1 flex flex-col justify-start mt-24">
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: accent }}>Offerta Commerciale</p>
              <div className="w-14 h-1 my-4" style={{ backgroundColor: accent }} />
              <h1 className="font-heading text-4xl font-black text-slate-900 leading-tight" data-testid="cover-title">
                {quote.cover_title || quote.title}
              </h1>
              {quote.cover_subtitle && (
                <p className="text-lg text-slate-500 mt-3">{quote.cover_subtitle}</p>
              )}
              {quote.cover_text && (
                <div className="mt-8 text-slate-700 leading-relaxed space-y-3 max-w-xl">
                  {quote.cover_text.split("\n").filter((p) => p.trim()).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              )}
            </div>
            {(f1 || f2) && (
              <div className="mt-auto pt-4 text-center text-[9.5px] text-slate-500 leading-relaxed"
                style={{ borderTop: `1.5px solid ${accent}` }}>
                {f1 && <p className="whitespace-nowrap">{f1}</p>}
                {f2 && <p className="whitespace-nowrap">{f2}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {hasNoleggio && (
        <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-lg shadow-sm mb-6 p-[48px]" data-testid="noleggio-cover">
          <p className="text-xs font-bold tracking-[0.2em] mb-6" style={{ color: accent }}>
            FORMULA NOLEGGIO OPERATIVO
          </p>
          {NOLEGGIO_COVER.map((sec, si) => (
            <div key={si} className="mb-7">
              <div className="w-10 h-[3px] mb-2.5" style={{ background: accent }} />
              <h2 className="font-heading text-xl font-extrabold text-slate-900 mb-2">{sec.title}</h2>
              {sec.paras.map((p, pi) => (
                <p key={pi} className="text-sm text-slate-700 leading-relaxed mb-2">{p}</p>
              ))}
              {sec.note && (
                <div className="text-sm font-semibold text-slate-900 bg-slate-100 px-4 py-3 mt-3"
                  style={{ borderLeft: `3px solid ${accent}` }}>
                  {sec.note}
                </div>
              )}
              {sec.bullets && (
                <ul className="mt-3 space-y-1.5">
                  {sec.bullets.map((b, bi) => (
                    <li key={bi} className="text-sm text-slate-700 leading-relaxed flex gap-2">
                      <span className="font-bold shrink-0" style={{ color: accent }}>✓</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Document */}
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-lg shadow-sm print-area flex flex-col min-h-[1123px]" data-testid="quote-document">
        <div className="p-[48px] flex flex-col flex-1">
          {/* Branded header */}
          <div className="flex items-center justify-between pb-6 mb-2 gap-6" style={{ borderBottom: `1.5px solid ${accent}` }}>
            <div className="flex items-center gap-5">
              {company.logo
                ? <img src={company.logo} alt="Logo" className="h-[72px] w-auto object-contain max-w-[300px]" data-testid="doc-company-logo" />
                : <span className="font-heading font-extrabold text-2xl text-slate-900">{company.company_name || "La tua azienda"}</span>}
              {quote.client_logo && (
                <>
                  <div className="w-px h-[72px] shrink-0" style={{ backgroundColor: accent }} />
                  <img src={quote.client_logo} alt="Logo cliente" className="h-[72px] w-auto object-contain max-w-[240px]" data-testid="doc-client-logo" />
                </>
              )}
            </div>
            {(quote.client_name || quote.client_email || quote.client_address) && (
              <div className="text-right max-w-[240px] shrink-0" data-testid="doc-recipient">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-1">Destinatario</p>
                {quote.client_name && <p className="font-semibold text-slate-900 text-sm leading-snug">{quote.client_name}</p>}
                {quote.client_address && <p className="text-xs text-slate-500 mt-0.5">{quote.client_address}</p>}
                {quote.client_email && <p className="text-xs text-slate-500 break-words">{quote.client_email}</p>}
              </div>
            )}
          </div>

          <div className="flex-1">
            <div className="flex items-start justify-between mt-4 mb-8">
              <h1 className="font-heading text-3xl font-black text-slate-900">{quote.title}</h1>
              <div className="text-right shrink-0 ml-4">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full border"
                  style={{ color: accent, borderColor: accent }}>{statusLabel[quote.status]}</span>
                <p className="font-mono text-sm text-slate-500 mt-2">{quote.number}</p>
              </div>
            </div>

            <table className="w-full mb-8">
              <thead>
                <tr className="border-y border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="text-left py-3">Voce</th>
                  <th className="text-center py-3 w-16">Qtà</th>
                  <th className="text-right py-3 w-28">Prezzo</th>
                  <th className="text-right py-3 w-28">Totale</th>
                </tr>
              </thead>
              <tbody>
                {(quote.items || []).map((it) => (
                  <tr key={it.id} className="border-b border-slate-100 align-top">
                    <td className="py-4 pr-4">
                      <div className="flex gap-3">
                        {it.image && (
                          <img src={it.image} alt={it.title} data-testid={`doc-item-image-${it.id}`}
                            className="w-16 h-16 object-cover rounded border border-slate-200 shrink-0" />
                        )}
                        <div>
                          <p className="font-semibold text-slate-900">
                            {it.title}
                            {itemAcq(it) === "noleggio" && (
                              <span className="ml-2 inline-block text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full border align-middle"
                                style={{ color: accent, borderColor: accent }} data-testid={`doc-item-noleggio-tag-${it.id}`}>
                                NOLEGGIO{itemMonths(it) ? ` · ${itemMonths(it)} mesi` : ""}
                              </span>
                            )}
                          </p>
                          {it.description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{it.description}</p>}
                          {(it.product_type === "server") ? (
                            (it.spec_rows || []).filter((r) => (r.label || "").trim() || (r.value || "").trim()).length > 0 && (
                              <div className="mt-2 space-y-0.5" data-testid={`doc-item-serverspecs-${it.id}`}>
                                {(it.spec_rows || [])
                                  .filter((r) => (r.label || "").trim() || (r.value || "").trim())
                                  .map((r, ri) => (
                                    <p key={ri} className="text-xs text-slate-500 leading-relaxed">
                                      <span className="font-semibold text-slate-700">{r.label}:</span> {r.value}
                                    </p>
                                  ))}
                              </div>
                            )
                          ) : (it.specs && SPEC_FIELDS.some(([k]) => it.specs[k]) && (
                            <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-0.5" data-testid={`doc-item-specs-${it.id}`}>
                              {SPEC_FIELDS.map(([k, lbl]) => (it.specs[k] ? (
                                <p key={k} className="text-xs text-slate-500">
                                  <span className="font-semibold text-slate-700">{lbl}:</span> {it.specs[k]}
                                </p>
                              ) : null))}
                            </div>
                          ))}
                          {it.warranty_months > 0 && (
                            <p className="text-xs text-slate-500 mt-2" data-testid={`doc-item-warranty-${it.id}`}>
                              <span className="font-semibold text-slate-700">Garanzia:</span> {it.warranty_months} mesi
                            </p>
                          )}
                          {Array.isArray(it.gifts) && it.gifts.length > 0 && (
                            <div className="mt-3" data-testid={`doc-item-gifts-${it.id}`}>
                              <p className="text-xs font-semibold text-teal-700 flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" /> In omaggio:</p>
                              <ul className="mt-1 space-y-0.5">
                                {it.gifts.map((g, gi) => (
                                  <li key={gi} className="text-xs text-slate-600 flex items-center gap-2">
                                    <span className="w-1 h-1 rounded-full bg-teal-500" /> {g}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 text-center text-slate-600">{it.quantity}</td>
                    <td className="py-4 text-right text-slate-600">
                      {fmt(it.price, c)}
                      {it.discount > 0 && (
                        <div className="text-xs font-semibold text-green-600" data-testid={`doc-item-discount-${it.id}`}>-{it.discount}% sconto</div>
                      )}
                    </td>
                    <td className="py-4 text-right font-semibold text-slate-900">{fmt(lineTotal(it), c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="space-y-8 mt-6" data-testid="summary-tables">
              {nolItems.length > 0 && (
                <div data-testid="summary-noleggio">
                  <h3 className="font-heading text-base font-bold text-slate-900 pb-1.5 mb-2 border-b-2" style={{ borderColor: accent }}>Materiale a noleggio</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                        <th className="text-left py-2">Prodotto</th>
                        <th className="text-right py-2 w-16">Qtà</th>
                        <th className="text-right py-2 w-28">Valore</th>
                        <th className="text-right py-2 w-16">Mesi</th>
                        <th className="text-right py-2 w-32">Canone / mese</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nolItems.map((it, i) => {
                        const m = itemMonths(it);
                        const fee = m ? lineTotal(it) / m : 0;
                        return (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="py-2 text-slate-700">{it.title}</td>
                            <td className="py-2 text-right">{it.quantity}</td>
                            <td className="py-2 text-right">{fmt(lineTotal(it), c)}</td>
                            <td className="py-2 text-right">{m || "—"}</td>
                            <td className="py-2 text-right font-semibold">{fmt(fee, c)} / mese</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex justify-between font-heading text-lg font-black pt-2 mt-1 border-t border-slate-200" style={{ color: accent }} data-testid="doc-monthly-fee">
                    <span>Canone mensile complessivo</span><span>{fmt(nolFee, c)} / mese</span>
                  </div>
                </div>
              )}
              {acqItems.length > 0 && (
                <div data-testid="summary-acquisto">
                  <h3 className="font-heading text-base font-bold text-slate-900 pb-1.5 mb-2 border-b-2" style={{ borderColor: accent }}>Materiale in acquisto</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                        <th className="text-left py-2">Prodotto</th>
                        <th className="text-right py-2 w-16">Qtà</th>
                        <th className="text-right py-2 w-28">Prezzo</th>
                        <th className="text-right py-2 w-28">Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acqItems.map((it, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-2 text-slate-700">{it.title}</td>
                          <td className="py-2 text-right">{it.quantity}</td>
                          <td className="py-2 text-right">{fmt(it.price, c)}</td>
                          <td className="py-2 text-right font-semibold">{fmt(lineTotal(it), c)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex justify-between font-heading text-lg font-black text-slate-900 pt-2 mt-1 border-t border-slate-200" data-testid="doc-total">
                    <span>Totale acquisto</span><span>{fmt(acqTotal, c)}</span>
                  </div>
                </div>
              )}
            </div>

            {quote.notes && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Note</p>
                <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{quote.notes}</p>
              </div>
            )}

            {quote.public_token && (
              <div className="mt-8 pt-6 border-t border-slate-200 text-center" data-testid="doc-showcase-cta">
                <p className="text-xs text-slate-500 mb-2.5">Scopri tutti i prodotti dell'offerta con foto e schede tecniche dettagliate.</p>
                <a href={`/vetrina/${quote.public_token}`} target="_blank" rel="noreferrer"
                  data-testid="doc-showcase-link"
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-white text-[11px] font-semibold leading-none no-underline shadow-sm hover:shadow transition-shadow"
                  style={{ backgroundColor: accent }}>
                  <Images className="w-3 h-3 shrink-0" />
                  <span>Sfoglia il catalogo prodotti &rarr;</span>
                </a>
              </div>
            )}
          </div>

          {/* Fiscal footer pinned to the bottom of the sheet */}
          {(f1 || f2) && (
            <div className="mt-auto pt-4 text-center text-[9.5px] text-slate-500 leading-relaxed"
              style={{ borderTop: `1.5px solid ${accent}` }} data-testid="doc-footer">
              {f1 && <p className="whitespace-nowrap">{f1}</p>}
              {f2 && <p className="whitespace-nowrap">{f2}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Closing / conditions sheet */}
      {quote.closing_enabled && (
        <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-lg shadow-sm flex flex-col min-h-[1123px] mt-6 print-area"
          data-testid="closing-sheet">
          <div className="p-[48px] flex flex-col flex-1">
            <div className="flex items-center pb-6 mb-2 gap-5" style={{ borderBottom: `1.5px solid ${accent}` }}>
              {company.logo
                ? <img src={company.logo} alt="Logo" className="h-[72px] w-auto object-contain max-w-[300px]" data-testid="closing-company-logo" />
                : <span className="font-heading font-extrabold text-2xl text-slate-900">{company.company_name || "La tua azienda"}</span>}
              {quote.client_logo && (
                <>
                  <div className="w-px h-[72px] shrink-0" style={{ backgroundColor: accent }} />
                  <img src={quote.client_logo} alt="Logo cliente" className="h-[72px] w-auto object-contain max-w-[240px]" data-testid="closing-client-logo" />
                </>
              )}
            </div>
            <h2 className="font-heading text-2xl font-black text-slate-900 mt-6 mb-5">{quote.closing_title || "Condizioni e accettazione"}</h2>
            <div className="border border-slate-200 rounded-md overflow-hidden max-w-md">
              {quote.valid_until && (
                <div className="flex justify-between px-4 py-2.5 border-b border-slate-100 text-sm text-slate-600">
                  <span>Offerta valida fino al</span><b className="text-slate-900">{fmtDate(quote.valid_until)}</b>
                </div>
              )}
              {quote.payment_terms && (
                <div className="flex justify-between px-4 py-2.5 border-b border-slate-100 text-sm text-slate-600">
                  <span>Pagamento</span><b className="text-slate-900">{quote.payment_terms}</b>
                </div>
              )}
              {quote.vat_note && (
                <div className="flex justify-between px-4 py-2.5 border-b border-slate-100 text-sm text-slate-600">
                  <span>IVA</span><b className="text-slate-900">{quote.vat_note}</b>
                </div>
              )}
              {hasNoleggio && (
                <div className="flex justify-between px-4 py-3 bg-slate-50 text-base font-black text-slate-900">
                  <span>Canone mensile complessivo</span><b style={{ color: accent }}>{fmt(nolFee, c)} / mese</b>
                </div>
              )}
              {acqItems.length > 0 && (
                <div className="flex justify-between px-4 py-3 bg-slate-50 text-base font-black text-slate-900">
                  <span>Totale acquisto</span><b style={{ color: accent }}>{fmt(acqTotal, c)}</b>
                </div>
              )}
            </div>
            {quote.closing_text && (
              <div className="mt-5 text-sm text-slate-700 leading-relaxed space-y-2 max-w-xl">
                {quote.closing_text.split("\n").filter((p) => p.trim()).map((p, i) => {
                  const idx = p.indexOf(":");
                  return idx > 0 && idx < 45 ? (
                    <p key={i}><span className="font-semibold text-slate-900">{p.slice(0, idx + 1)}</span>{p.slice(idx + 1)}</p>
                  ) : <p key={i}>{p}</p>;
                })}
              </div>
            )}
            <div className="grid grid-cols-2 gap-10 mt-12">
              <div>
                <p className="text-xs text-slate-500 mb-6">{[quote.signing_place, new Date().toLocaleDateString("it-IT")].filter(Boolean).join(", ")}</p>
                <p className="text-sm font-bold text-slate-900 mb-2">{company.legal_name || company.company_name}</p>
                {company.signature_image
                  ? <img src={company.signature_image} alt="Timbro e firma" className="max-h-28 max-w-full mb-2" data-testid="closing-company-signature" />
                  : <div className="border-t border-slate-400 mt-10" />}
                <p className="text-[11px] text-slate-400 mt-1">Timbro e firma</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-6">Per accettazione</p>
                <p className="text-sm font-bold text-slate-900 mb-10 min-h-[40px]">Letto, confermato e sottoscritto, quindi diventa ordine</p>
                <div className="border-t border-slate-400" />
                <p className="text-[11px] text-slate-400 mt-1">Data e firma del cliente</p>
              </div>
            </div>
            {(f1 || f2) && (
              <div className="mt-auto pt-4 text-center text-[9.5px] text-slate-500 leading-relaxed"
                style={{ borderTop: `1.5px solid ${accent}` }}>
                {f1 && <p className="whitespace-nowrap">{f1}</p>}
                {f2 && <p className="whitespace-nowrap">{f2}</p>}
              </div>
            )}
          </div>
        </div>
      )}
      <p className="max-w-3xl mx-auto text-center text-xs text-slate-400 mt-4 no-print">
        La numerazione delle pagine viene aggiunta automaticamente nel PDF esportato.
      </p>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle>Condividi la Vetrina prodotti</DialogTitle>
            <DialogDescription>
              Invia ai tuoi clienti la vetrina interattiva di questa offerta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input readOnly value={shareUrl} data-testid="share-link-input"
                className="font-mono text-xs" onFocus={(e) => e.target.select()} />
              <Button variant="outline" size="icon" onClick={copyShareLink} data-testid="share-copy-btn" title="Copia link">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={shareWhatsApp} data-testid="share-whatsapp-btn"
                className="bg-[#25D366] hover:bg-[#1ebe57] text-white">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
              <Button onClick={shareEmail} variant="outline" data-testid="share-email-btn">
                <Mail className="w-4 h-4 mr-2" /> Email
              </Button>
            </div>
            <a href={showcaseUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline font-medium"
              data-testid="share-preview-link">
              <ExternalLink className="w-4 h-4" /> Apri anteprima vetrina
            </a>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="email-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invia offerta via email</DialogTitle>
            <DialogDescription>Il PDF dell'offerta verrà allegato automaticamente e inviato dal tuo Microsoft 365.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Destinatario</Label>
              <Input data-testid="email-to" type="email" value={emailData.to}
                onChange={(e) => setEmailData((d) => ({ ...d, to: e.target.value }))}
                placeholder="email@cliente.it" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">CC (opzionale)</Label>
              <Input data-testid="email-cc" value={emailData.cc}
                onChange={(e) => setEmailData((d) => ({ ...d, cc: e.target.value }))}
                placeholder="commerciale@86bit.it" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Oggetto</Label>
              <Input data-testid="email-subject" value={emailData.subject}
                onChange={(e) => setEmailData((d) => ({ ...d, subject: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Messaggio</Label>
              <Textarea data-testid="email-body" rows={9} value={emailData.body}
                onChange={(e) => setEmailData((d) => ({ ...d, body: e.target.value }))} className="mt-1" />
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <FileDown className="w-4 h-4" /> Allegato: {quote.number || "preventivo"}.pdf
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setEmailOpen(false)} data-testid="email-cancel">Annulla</Button>
            <Button onClick={sendEmail} disabled={emailSending} data-testid="email-send" className="bg-blue-600 hover:bg-blue-700">
              {emailSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Invia
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
